import { LitElement, html, css } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import sanitizeHtml from 'sanitize-html'

import { assumeExhaustion } from '../lib/typeHelpers.no-deps'
import type { NotificationReason, ProfileCircleColor, SupportedLocale } from 'zaptic-types'

import {
    booleanAttributeSchema,
    dateAttributeSchema,
    localeAttributeSchema,
    notificationReasonAttributeSchema,
    profileCircleColorAttributeSchema,
} from './helpers/yup'
import type { Translations } from '../i18n/Translations'
import { ifDefined } from 'lit/directives/if-defined.js'
import { unsafeHTML } from 'lit/directives/unsafe-html.js'

declare const window: Window &
    typeof globalThis & {
        i18n: typeof Translations.t
    }

@customElement('zaptic-notification-item')
export class NotificationItem extends LitElement {
    @property({ attribute: 'created-at', converter: (val) => dateAttributeSchema.validateSync(val) })
    private createdAt: Date

    @property({ attribute: 'user-name', converter: (val) => val ?? '' })
    private userName = ''

    @property({ attribute: 'user-color', converter: (val) => profileCircleColorAttributeSchema.validateSync(val) })
    private userColor: ProfileCircleColor

    @property({ attribute: 'user-profile-image-url' })
    private userProfileImageUrl: string

    @property({ attribute: 'locale', converter: (val) => localeAttributeSchema.validateSync(val) })
    private locale: SupportedLocale

    @property({ attribute: 'reason', converter: (val) => notificationReasonAttributeSchema.validateSync(val) })
    private reason: NotificationReason

    @property({ attribute: 'read', type: Boolean, converter: (val) => booleanAttributeSchema.validateSync(val) })
    private read: boolean

    @property({ attribute: 'assignee-name' })
    private assigneeName: string

    @property({ attribute: 'task-name' })
    private taskName: string = ''

    @property({ attribute: 'old-report-status-name' })
    private oldReportStatusName: string

    @property({ attribute: 'new-report-status-name' })
    private newReportStatusName: string

    @property({ attribute: 'is-report-status-final', converter: { fromAttribute: (value) => value === 'true' } })
    private isReportStatusFinal: boolean = false

    static styles = css`
        .item {
            display: flex;
            padding: 20px;
            cursor: pointer;
        }

        .item.unread {
            background-color: var(--purple-100);
        }

        zaptic-profile-circle {
            --ProfileCircle-size: 32px;
            --ProfileCircle-paddingRight: 12px;
            flex-shrink: 0;
        }

        zaptic-relative-time {
            color: var(--grey3);
            font-size: 14px;
            margin-top: 5px;
        }

        #reason {
            font-size: 16px;
            line-height: 1.3;
            color: var(--darkGrey);
        }

        b {
            font-weight: var(--font-weight-bold);
        }
    `

    render() {
        const reasonHtml = this.reasonHtml()
        if (reasonHtml == null) return html``

        return html`
            <div class="item ${this.read ? 'read' : 'unread'}">
                <zaptic-profile-circle
                    name="${this.userName}"
                    color="${ifDefined(this.userColor)}"
                    profile-image-url="${ifDefined(this.userProfileImageUrl)}"
                ></zaptic-profile-circle>
                <div>
                    <div id="reason">${this.reasonHtml()}</div>
                    <zaptic-relative-time time=${this.createdAt} locale=${this.locale}> </zaptic-relative-time>
                </div>
            </div>
        `
    }

    private reasonHtml() {
        const user = ` <b>${this.userName}</b> `
        const assignee = ` <b>${this.assigneeName}</b> `
        const taskName = ` <b>${this.taskName}</b> `

        switch (this.reason) {
            case 'AssignedAnActionToYou':
                return html`${unsafeHTML(
                    window.i18n(
                        sanitizeHtml(window.i18n('shared:notificationItem.assignedAnActionToYou'), { allowedTags: [] }),
                        { user, b: '<b>', eb: '</b>' }
                    ) as string
                )}`
            case 'AssignedYourActionToSomeoneElse':
                if (this.assigneeName == null) return html``

                return html`${unsafeHTML(
                    window.i18n(
                        sanitizeHtml(window.i18n('shared:notificationItem.assignedYourActionToSomeoneElse'), {
                            allowedTags: [],
                        }),
                        {
                            user,
                            assignee,
                            b: '<b>',
                            eb: '</b>',
                        }
                    ) as string
                )}`

            case 'AssignedAnActionYouCreatedToSomeoneElse':
                if (this.assigneeName == null) return html``

                return html`${unsafeHTML(
                    window.i18n(
                        sanitizeHtml(window.i18n('shared:notificationItem.assignedAnActionYouCreatedToSomeoneElse'), {
                            allowedTags: [],
                        }),
                        {
                            user,
                            assignee,
                            b: '<b>',
                            eb: '</b>',
                        }
                    ) as string
                )}`

            case 'ChangedStatusOfYourReport':
                if (this.newReportStatusName == null || this.oldReportStatusName == null) return ``

                const oldReportStatusName = ` <b>${this.oldReportStatusName}</b> `
                const newReportStatusName = ` <b>${this.newReportStatusName}</b> `

                return html`${unsafeHTML(
                    window.i18n(
                        sanitizeHtml(window.i18n('shared:notificationItem.changedStatusOfYourReport'), {
                            allowedTags: [],
                        }),
                        {
                            user,
                            oldReportStatusName,
                            newReportStatusName,
                            b: '<b>',
                            eb: '</b>',
                        }
                    ) as string
                )}`

            case 'CommentedOnAPostYouFollow':
                return html`${unsafeHTML(
                    window.i18n(
                        sanitizeHtml(window.i18n('shared:notificationItem.commentedOnAPostYouFollow'), {
                            allowedTags: [],
                        }),
                        {
                            user,
                            b: '<b>',
                            eb: '</b>',
                        }
                    ) as string
                )}`

            case 'CommentedOnYourPost':
                return html`${unsafeHTML(
                    window.i18n(
                        sanitizeHtml(window.i18n('shared:notificationItem.commentedOnYourPost'), { allowedTags: [] }),
                        {
                            user,
                            b: '<b>',
                            eb: '</b>',
                        }
                    ) as string
                )}`

            case 'CompletedActionYouCreated':
                return html`${unsafeHTML(
                    window.i18n(
                        sanitizeHtml(window.i18n('shared:notificationItem.completedActionYouCreated'), {
                            allowedTags: [],
                        }),
                        {
                            user,
                            b: '<b>',
                            eb: '</b>',
                        }
                    ) as string
                )}`

            case 'CompletedAnActionYouCreatedForAssignee':
                if (this.assigneeName == null) return html``

                return html`${unsafeHTML(
                    window.i18n(
                        sanitizeHtml(window.i18n('shared:notificationItem.completedAnActionYouCreatedForAssignee'), {
                            allowedTags: [],
                        }),
                        {
                            user,
                            b: '<b>',
                            eb: '</b>',
                        }
                    ) as string
                )}`

            case 'CompletedAnActionForYou':
                return html`${unsafeHTML(
                    window.i18n(
                        sanitizeHtml(window.i18n('shared:notificationItem.completedAnActionForYou'), {
                            allowedTags: [],
                        }),
                        {
                            user,
                            b: '<b>',
                            eb: '</b>',
                        }
                    ) as string
                )}`

            case 'MentionedYou':
                return html`${unsafeHTML(
                    window.i18n(
                        sanitizeHtml(window.i18n('shared:notificationItem.mentionedYou'), { allowedTags: [] }),
                        { user, b: '<b>', eb: '</b>' }
                    ) as string
                )}`

            case 'RemovedActionAssignedToYou':
                return html`${unsafeHTML(
                    window.i18n(
                        sanitizeHtml(window.i18n('shared:notificationItem.removedActionAssignedToYou'), {
                            allowedTags: [],
                        }),
                        {
                            user,
                            b: '<b>',
                            eb: '</b>',
                        }
                    ) as string
                )}`

            case 'RemovedActionYouCreated':
                return html`${unsafeHTML(
                    window.i18n(
                        sanitizeHtml(window.i18n('shared:notificationItem.removedActionYouCreated'), {
                            allowedTags: [],
                        }),
                        {
                            user,
                            b: '<b>',
                            eb: '</b>',
                        }
                    ) as string
                )}`

            case 'UpdatedAnActionAssignedToYou':
                return html`${unsafeHTML(
                    window.i18n(
                        sanitizeHtml(window.i18n('shared:notificationItem.updatedAnActionAssignedToYou'), {
                            allowedTags: [],
                        }),
                        {
                            user,
                            b: '<b>',
                            eb: '</b>',
                        }
                    ) as string
                )}`

            case 'UpdatedAnActionYouCreated':
                return html`${unsafeHTML(
                    window.i18n(
                        sanitizeHtml(window.i18n('shared:notificationItem.updatedAnActionYouCreated'), {
                            allowedTags: [],
                        }),
                        {
                            user,
                            b: '<b>',
                            eb: '</b>',
                        }
                    ) as string
                )}`

            case 'EditedYourReport':
                return html`${window.i18n('shared:notificationItem.editedYourReport')}`

            case 'AssignedTaskWithTrainingToYou':
                return html`${unsafeHTML(
                    window.i18n(
                        sanitizeHtml(window.i18n('shared:notificationItem.assignedTaskWithTrainingToYou'), {
                            allowedTags: [],
                        }),
                        {
                            user,
                            task: taskName,
                        }
                    ) as string
                )}`
            case `ReportReadyForReview`:
                const translation = this.isReportStatusFinal
                    ? window.i18n('shared:notificationItem.reportStatusIsFinal')
                    : window.i18n('shared:notificationItem.reportReadyForReview')

                return html`${unsafeHTML(
                    window.i18n(
                        sanitizeHtml(translation, {
                            allowedTags: [],
                        }),
                        {
                            user,
                            b: '<b>',
                            eb: '</b>',
                            newReportStatusName: ` <b>${this.newReportStatusName}</b> `,
                            task: taskName,
                        }
                    ) as string
                )}`

            default:
                assumeExhaustion(this.reason)
        }
    }
}
